import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { useState, useEffect } from 'react'
import { Link } from 'wouter'
import { FileText, Eye, MessageSquare, Calendar, DollarSign, Search, Filter, Plus, Users, TrendingUp, Edit, Trash2, ArrowLeft, ArrowRight, Upload } from 'lucide-react'

import toast from 'react-hot-toast'
import { buildApiUrl } from '@/config/api'

interface Rfp {
  id: string
  title: string
  description: string
  requirements: string[]
  budgetMin: number
  budgetMax: number
  budgetCurrency: string
  deadline: string
  category: string
  status: string
  views: number
  responses: number
  linkedinShares: number
  createdAt: string
  updatedAt: string
  user: {
    id: string
    name: string
    company: string
    role: string
  }
}

interface Pagination {
  page: number
  limit: number
  total: number
  totalPages: number
  hasNext: boolean
  hasPrev: boolean
}

export function MyRfps() {

  const [rfps, setRfps] = useState<Rfp[]>([])
  const [pagination, setPagination] = useState<Pagination | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState('all')
  const [categoryFilter, setCategoryFilter] = useState('all')
  const [currentPage, setCurrentPage] = useState(1)
  const [publishingRfpId, setPublishingRfpId] = useState<string | null>(null)

  const fetchMyRfps = async (page = 1) => {
    try {
      const token = localStorage.getItem('token')
      if (!token) {
        toast.error('Please log in to view your RFPs')
        return
      }

      const params = new URLSearchParams({
        page: page.toString(),
        limit: '10',
        myRfps: 'true', // This will filter for user's own RFPs
      })

      if (searchTerm) params.append('search', searchTerm)
      if (statusFilter !== 'all') params.append('status', statusFilter)
      if (categoryFilter !== 'all') params.append('category', categoryFilter)

      const response = await fetch(buildApiUrl(`/api/rfps?${params}`), {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      })

      if (response.ok) {
        const data = await response.json()
        setRfps(data.data || [])
        setPagination(data.pagination || null)
      } else if (response.status === 404) {
        // Treat 404 as "no RFPs yet" instead of an error
        setRfps([])
        setPagination(null)
      } else {
        toast.error('Unable to load your RFPs. Please try again.')
      }
    } catch (error) {
      console.error('Error fetching RFPs:', error)
      toast.error('Unable to load your RFPs. Please check your connection and try again.')
    } finally {
      setIsLoading(false)
    }
  }

  const deleteRfp = async (rfpId: string) => {
    if (!confirm('Are you sure you want to delete this RFP? This action cannot be undone.')) {
      return
    }

    try {
      const token = localStorage.getItem('token')
      if (!token) {
        toast.error('Please log in to view your RFPs')
        return
      }

      const response = await fetch(buildApiUrl(`/api/rfps/${rfpId}`), {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      })

      if (response.ok) {
        setRfps(prev => prev.filter(rfp => rfp.id !== rfpId))
        toast.success('RFP deleted successfully')
        
        // Refresh the current page if it becomes empty
        if (rfps.length === 1 && currentPage > 1) {
          setCurrentPage(prev => prev - 1)
        } else {
          fetchMyRfps(currentPage)
        }
      } else {
        const errorData = await response.json()
        toast.error(errorData.error || 'Failed to delete RFP')
      }
    } catch (error) {
      console.error('Error deleting RFP:', error)
      toast.error('Failed to delete RFP')
    }
  }

  const publishRfp = async (rfpId: string) => {
    setPublishingRfpId(rfpId)
    try {
      const token = localStorage.getItem('token')
      if (!token) {
        toast.error('Please log in to publish your RFP')
        return
      }

      const response = await fetch(buildApiUrl(`/api/rfps/${rfpId}/publish`), {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      })

      if (response.ok) {
        // Update the RFP status in the local state
        setRfps(prev => prev.map(rfp => 
          rfp.id === rfpId ? { ...rfp, status: 'PUBLISHED' } : rfp
        ))
        toast.success('RFP published successfully!')
      } else {
        const errorData = await response.json()
        if (response.status === 400) {
          toast.error(errorData.error || 'Only draft RFPs can be published')
        } else if (response.status === 404) {
          toast.error('RFP not found')
        } else if (response.status === 401) {
          toast.error('Please log in to publish your RFP')
        } else {
          toast.error(errorData.error || 'Failed to publish RFP')
        }
      }
    } catch (error) {
      console.error('Error publishing RFP:', error)
      if (error instanceof TypeError && error.message.includes('fetch')) {
        toast.error('Unable to connect to the server. Please check your internet connection and try again.')
      } else {
        toast.error('An unexpected error occurred. Please try again.')
      }
    } finally {
      setPublishingRfpId(null)
    }
  }

  useEffect(() => {
    fetchMyRfps(currentPage)
  }, [currentPage, searchTerm, statusFilter, categoryFilter])

  const handleSearch = (value: string) => {
    setSearchTerm(value)
    setCurrentPage(1)
  }

  const handleStatusFilter = (value: string) => {
    setStatusFilter(value)
    setCurrentPage(1)
  }

  const handleCategoryFilter = (value: string) => {
    setCategoryFilter(value)
    setCurrentPage(1)
  }

  const formatCurrency = (amount: number, currency: string) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount)
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    })
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'PUBLISHED':
        return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
      case 'DRAFT':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
      case 'CLOSED':
        return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
      case 'AWARDED':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200'
    }
  }

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'PUBLISHED':
        return <TrendingUp className="h-4 w-4" />
      case 'DRAFT':
        return <FileText className="h-4 w-4" />
      case 'CLOSED':
        return <Calendar className="h-4 w-4" />
      case 'AWARDED':
        return <Users className="h-4 w-4" />
      default:
        return <FileText className="h-4 w-4" />
    }
  }

  if (isLoading) {
    return (
      <div className="space-y-8">
        <div className="flex items-center space-x-4">
          <Link href="/">
            <Button variant="ghost" size="icon">
              <ArrowLeft className="h-4 w-4" />
            </Button>
          </Link>
          <div>
            <h1 className="text-3xl font-bold tracking-tight">My RFPs</h1>
            <p className="text-muted-foreground">
              Manage the RFPs you've created and track vendor responses
            </p>
          </div>
        </div>
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
            <p className="mt-2 text-muted-foreground">Loading your RFPs...</p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Link href="/">
            <Button variant="ghost" size="icon">
              <ArrowLeft className="h-4 w-4" />
            </Button>
          </Link>
          <div>
            <h1 className="text-3xl font-bold tracking-tight">My RFPs</h1>
            <p className="text-muted-foreground">
              Manage the RFPs you've created and track vendor responses
            </p>
          </div>
        </div>
        <div className="flex items-center space-x-2">
          {pagination && (
            <div className="text-sm text-muted-foreground">
              {pagination.total} RFPs created
            </div>
          )}
          <Link href="/create">
            <Button>
              <Plus className="mr-2 h-4 w-4" />
              Create New RFP
            </Button>
          </Link>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Filter className="h-5 w-5" />
            <span>Filters</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">Search</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search your RFPs..."
                  value={searchTerm}
                  onChange={(e) => handleSearch(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Status</label>
              <select
                value={statusFilter}
                onChange={(e) => handleStatusFilter(e.target.value)}
                className="w-full px-3 py-2 border border-input rounded-md bg-background"
              >
                <option value="all">All Statuses</option>
                <option value="PUBLISHED">Published</option>
                <option value="DRAFT">Draft</option>
                <option value="CLOSED">Closed</option>
                <option value="AWARDED">Awarded</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Category</label>
              <select
                value={categoryFilter}
                onChange={(e) => handleCategoryFilter(e.target.value)}
                className="w-full px-3 py-2 border border-input rounded-md bg-background"
              >
                <option value="all">All Categories</option>
                <option value="Technology">Technology</option>
                <option value="Marketing">Marketing</option>
                <option value="Design">Design</option>
                <option value="Development">Development</option>
                <option value="Consulting">Consulting</option>
                <option value="Other">Other</option>
              </select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* RFP List */}
      {rfps.length === 0 ? (
        <Card>
          <CardContent className="text-center py-12">
            <FileText className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-medium mb-2">No RFPs created yet</h3>
            <p className="text-muted-foreground mb-4">
              Start creating RFPs to find vendors for your projects
            </p>
            <Link href="/create">
              <Button>
                <Plus className="mr-2 h-4 w-4" />
                Create Your First RFP
              </Button>
            </Link>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {rfps.map((rfp) => (
            <Card key={rfp.id} className="hover:shadow-md transition-shadow">
              <CardContent className="p-6">
                <div className="flex items-start justify-between">
                  <div className="flex items-start space-x-4 flex-1 min-w-0">
                    <div className="h-12 w-12 rounded-lg gradient-bg flex items-center justify-center flex-shrink-0">
                      {getStatusIcon(rfp.status)}
                    </div>
                    <div className="flex-1 min-w-0 overflow-hidden">
                      <div className="flex items-center space-x-2 mb-2">
                        <Link href={`/rfp/${rfp.id}`} className="hover:underline min-w-0 flex-1">
                          <h3 className="text-lg font-semibold truncate text-primary hover:text-primary/80 transition-colors">
                            {rfp.title}
                          </h3>
                        </Link>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(rfp.status)}`}>
                          {rfp.status.toLowerCase()}
                        </span>
                      </div>
                      
                      <p className="text-muted-foreground mb-4 line-clamp-2 overflow-hidden text-ellipsis break-words">
                        {rfp.description}
                      </p>

                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                        <div className="flex items-center space-x-2">
                          <DollarSign className="h-4 w-4 text-muted-foreground" />
                          <span className="text-sm">
                            {formatCurrency(rfp.budgetMin, rfp.budgetCurrency)} - {formatCurrency(rfp.budgetMax, rfp.budgetCurrency)}
                          </span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Calendar className="h-4 w-4 text-muted-foreground" />
                          <span className="text-sm">Due {formatDate(rfp.deadline)}</span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Eye className="h-4 w-4 text-muted-foreground" />
                          <span className="text-sm">{rfp.views} views</span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <MessageSquare className="h-4 w-4 text-muted-foreground" />
                          <span className="text-sm">{rfp.responses} responses</span>
                        </div>
                      </div>

                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-2 text-sm text-muted-foreground">
                          <span>Created {formatDate(rfp.createdAt)}</span>
                          <span>•</span>
                          <span>Category: {rfp.category}</span>
                          {rfp.linkedinShares > 0 && (
                            <>
                              <span>•</span>
                              <span>{rfp.linkedinShares} LinkedIn shares</span>
                            </>
                          )}
                        </div>
                        <div className="flex items-center space-x-2">
                          {rfp.status === 'DRAFT' && (
                            <Button
                              variant="default"
                              size="sm"
                              onClick={() => publishRfp(rfp.id)}
                              disabled={publishingRfpId === rfp.id}
                              className="bg-green-600 hover:bg-green-700 text-white disabled:opacity-50"
                            >
                              {publishingRfpId === rfp.id ? (
                                <>
                                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                                  Publishing...
                                </>
                              ) : (
                                <>
                                  <Upload className="mr-2 h-4 w-4" />
                                  Publish
                                </>
                              )}
                            </Button>
                          )}
                          <Link href={`/rfp/${rfp.id}/responses`}>
                            <Button variant="outline" size="sm">
                              <MessageSquare className="mr-2 h-4 w-4" />
                              View Responses ({rfp.responses})
                            </Button>
                          </Link>
                          <Link href={`/rfp/${rfp.id}`}>
                            <Button variant="outline" size="sm">
                              <Eye className="mr-2 h-4 w-4" />
                              View Details
                            </Button>
                          </Link>
                          <Link href={`/rfp/${rfp.id}/edit`}>
                            <Button variant="outline" size="sm">
                              <Edit className="mr-2 h-4 w-4" />
                              Edit
                            </Button>
                          </Link>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => deleteRfp(rfp.id)}
                            className="text-red-600 hover:text-red-700"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Pagination */}
      {pagination && pagination.totalPages > 1 && (
        <div className="flex items-center justify-between">
          <div className="text-sm text-muted-foreground">
            Showing {((pagination.page - 1) * pagination.limit) + 1} to {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total} RFPs
          </div>
          <div className="flex items-center space-x-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(pagination.page - 1)}
              disabled={!pagination.hasPrev}
            >
              <ArrowLeft className="h-4 w-4" />
              Previous
            </Button>
            <span className="text-sm">
              Page {pagination.page} of {pagination.totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(pagination.page + 1)}
              disabled={!pagination.hasNext}
            >
              Next
              <ArrowRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  )
} 